// ink-component
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import { MonologueWrapper, MonologueScroll, StyledMonologue, MonologueBody } from './styles';

export interface MonologueProps {
  /** Content */
  children: ReactNode;
  /** Overwrites the default height. */
  height?: 'small' | 'medium' | 'large' | 'full';
  /** @deprecated:Box You can replicate this behavior using Box's top prop */
  marginTop?: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10';
  /** @deprecated:Box You can replicate this behavior using Box's bottom prop */
  marginBottom?: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10';
  /** Preserves children's original text formatting, including line breaks and indentation. */
  preserveFormatting?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/**
 * @purpose Presents long text
 * @categories layout
 * @aka scrollable text, terms, textblock, legal text
 */
function Monologue(props: MonologueProps) {
  const { height, marginTop, marginBottom, preserveFormatting } = props;

  let globalProps = getGlobalProps({});

  if (marginTop || marginBottom) {
    globalProps = getGlobalProps({ deprecatedProps: true });
  }

  if (marginTop) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `marginTop` prop in Monologue is deprecated. Wrap it in a `Box` instead. https://ink.carta.com/components/Box',
    );
  }

  if (marginBottom) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `marginBottom` prop in Monologue is deprecated. Wrap it in a `Box` instead. https://ink.carta.com/components/Box',
    );
  }

  const [scrollTop, setScrollTop] = useState(0);
  const [scrollBottom, setScrollBottom] = useState(0);
  const ref = useRef(null);

  const setScrollState = target => {
    const { scrollTop, scrollHeight, clientHeight } = target;
    setScrollTop(scrollTop);
    setScrollBottom(scrollTop - (scrollHeight - clientHeight));
  };

  useEffect(() => {
    setScrollState(ref.current);
  }, []);

  const handleScroll = e => {
    setScrollState(e.target);
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        monologueMarginBottom: marginBottom,
        monologueMarginTop: marginTop,
        monologueTop: scrollTop <= 0,
        monologueBottom: scrollBottom >= -1,
        monologuePreserveFormatting: preserveFormatting,
        monologueHeight: height,
      })}
    >
      <MonologueWrapper {...globalProps} data-testid={props['data-testid']}>
        <MonologueScroll onScroll={handleScroll} ref={ref}>
          <StyledMonologue>
            <MonologueBody>{props.children}</MonologueBody>
          </StyledMonologue>
        </MonologueScroll>
      </MonologueWrapper>
    </ThemeProvider>
  );
}

Monologue.defaultProps = {
  height: 'medium',
  marginTop: '0',
  marginBottom: '1',
  preserveFormatting: false,
  'data-testid': undefined,
};

Monologue.displayName = 'Monologue';

export default Monologue;
